import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Facilities.css';
import { useCache } from '../hooks/useCache';
import client from '../api/client';
import AnalysisShortcutBar from '../components/AnalysisShortcutBar';
import DateRangePicker from '../components/DateRangePicker';
import { getActivityCount } from '../utils/chartBinning';
import * as XLSX from 'xlsx';
import { triggerDownload } from '../utils/exportData';

// All Facilities — read-only browser of the cached Salesforce facility
// data, in either flat-list or hierarchy form. Refresh is intentionally
// NOT exposed here: the global FreshnessBanner at the top of the app
// already drives Salesforce sync.

const DATE_PRESET_LABELS = {
  last_week: 'Last 7 days',
  last_month: 'Last 30 days',
  last_3_months: 'Last 3 months',
  last_6_months: 'Last 6 months',
  last_year: 'Last 12 months',
  ytd: 'Year to date',
  custom: 'Custom range',
};

const EXPORT_COLUMNS = [
  ['Salesforce ID', (f) => f.id],
  ['Name', (f) => f.name],
  ['VitalEngine Facility ID', (f) => f.vitalEngine_Facility_ID__c],
  ['Parent ID', (f) => f.parentId],
  ['Type', (f) => f.type],
  ['Billing Street', (f) => f.billingStreet],
  ['Billing City', (f) => f.billingCity],
  ['Billing State', (f) => f.billingState],
  ['Billing Postal Code', (f) => f.billingPostalCode],
  ['Billing Country', (f) => f.billingCountry],
  ['Latitude', (f) => f.billingLatitude],
  ['Longitude', (f) => f.billingLongitude],
  ['Geocode Source', (f) => f.geocodeSource],
  ['Geocoded At', (f) => f.geocodedAt],
  ['IDN', (f) => f.idN__c],
  ['Installation Date', (f) => f.vitalEngine_Installation_Date__c],
  ['Customer Segment', (f) => f.customer_Segment__c],
  ['Number of Beds', (f) => f.number_of_Beds__c],
  ['EMR Type', (f) => f.emR_Type__c],
  ['Specialty', (f) => f.specialty__c],
  ['Owner ID', (f) => f.ownerId],
  ['Owner Name', (f) => f.ownerName],
  ['Owner Email', (f) => f.ownerEmail],
  ['Customer Success ID', (f) => f.customer_Success__c],
  ['Customer Success Name', (f) => f.customerSuccessName],
  ['Customer Success Email', (f) => f.customerSuccessEmail],
  ['Sales Rep ID', (f) => f.sales_Representative__c],
  ['Sales Rep Name', (f) => f.salesRepName],
  ['Sales Rep Email', (f) => f.salesRepEmail],
  ['Logins in Range', (f) => f._loginCount],
];

function formatYmd(date) {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function calculateDateRange(preset, customStart, customEnd) {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case 'last_week':
      start.setDate(start.getDate() - 7);
      break;
    case 'last_month':
      start.setDate(start.getDate() - 30);
      break;
    case 'last_3_months':
      start.setMonth(start.getMonth() - 3);
      break;
    case 'last_6_months':
      start.setMonth(start.getMonth() - 6);
      break;
    case 'last_year':
      start.setFullYear(start.getFullYear() - 1);
      break;
    case 'ytd':
      start.setMonth(0, 1);
      break;
    case 'custom':
      return {
        start: customStart ? new Date(customStart) : start,
        end: customEnd ? new Date(customEnd) : end,
      };
    default:
      start.setDate(start.getDate() - 30);
  }
  return { start, end };
}

function formatFacilityId(id) {
  if (id == null || id === '') return null;
  const num = parseFloat(id);
  if (Number.isNaN(num)) return String(id);
  return Number.isInteger(num) ? num.toString() : Math.round(num).toString();
}

function Facilities() {
  const { getCachedFacilities, getCachedFacilityHierarchy, getCachedData } = useCache();

  const [activeTab, setActiveTab] = useState('list');
  const [facilities, setFacilities] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedHierarchy, setExpandedHierarchy] = useState(new Set());
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMissingAddressOnly, setShowMissingAddressOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Shared filters (list + hierarchy)
  const [activeFilterEnabled, setActiveFilterEnabled] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState('last_month');
  const [customStartDate, setCustomStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatYmd(d);
  });
  const [customEndDate, setCustomEndDate] = useState(() => formatYmd(new Date()));
  const [minLogins, setMinLogins] = useState(1);
  const [loginCounts, setLoginCounts] = useState(() => new Map());
  const [loginsLoading, setLoginsLoading] = useState(false);

  const [payingOnly, setPayingOnly] = useState(false);
  const [payingFacilityIds, setPayingFacilityIds] = useState(() => new Set());
  const [payingLoading, setPayingLoading] = useState(false);

  const [csRepFilter, setCsRepFilter] = useState(''); // '' = all, '__unassigned__', or name

  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => calculateDateRange(dateRangePreset, customStartDate, customEndDate),
    [dateRangePreset, customStartDate, customEndDate],
  );
  const startDate = formatYmd(rangeStart);
  const endDate = formatYmd(rangeEnd);

  useEffect(() => {
    loadFromCache();
    loadPayingFacilityIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeFilterEnabled) {
      setLoginCounts(new Map());
      setLoginsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoginsLoading(true);
      try {
        const res = await getCachedData('logins', startDate, endDate);
        if (cancelled) return;
        const records = res?.records || [];
        const counts = new Map();
        for (const record of records) {
          const inner = record.data || record.Data || {};
          const fid = formatFacilityId(inner.facility_id ?? inner.Facility_Id ?? inner.facilityId);
          if (!fid) continue;
          const n = getActivityCount(record, 'logins');
          counts.set(fid, (counts.get(fid) || 0) + (Number.isFinite(n) ? n : 0));
        }
        setLoginCounts(counts);
      } catch (e) {
        if (!cancelled) {
          setError(`Failed to load logins for active filter: ${e.message || 'request error'}`);
          setLoginCounts(new Map());
        }
      } finally {
        if (!cancelled) setLoginsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeFilterEnabled, startDate, endDate, getCachedData]);

  const loadPayingFacilityIds = async () => {
    setPayingLoading(true);
    try {
      const res = await client.get('/api/customers/all-customer-facility-ids');
      const ids = res.data?.facilityIds || [];
      setPayingFacilityIds(new Set(ids.map((id) => formatFacilityId(id)).filter(Boolean)));
    } catch {
      // Non-fatal — paying filter simply won't match until reload succeeds
      setPayingFacilityIds(new Set());
    } finally {
      setPayingLoading(false);
    }
  };

  const loadFromCache = async () => {
    setLoading(true);
    setError('');
    try {
      const [facilitiesResponse, hierarchyResponse] = await Promise.all([
        getCachedFacilities(),
        getCachedFacilityHierarchy(),
      ]);

      if (facilitiesResponse?.success) {
        setFacilities(facilitiesResponse.facilities || []);
      }
      if (hierarchyResponse?.success) {
        setHierarchy(hierarchyResponse.hierarchy || []);
      }

      if ((!facilitiesResponse?.success || facilitiesResponse.facilities?.length === 0) &&
          (!hierarchyResponse?.success || hierarchyResponse.hierarchy?.length === 0)) {
        setError('No cached facilities. Use the refresh bar at the top of the app to sync from Salesforce.');
      }
    } catch {
      setError('No cached facilities found. Use the refresh bar at the top of the app to sync from Salesforce.');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const facilityByVeId = useMemo(() => {
    const map = new Map();
    for (const f of facilities) {
      const vid = formatFacilityId(f.vitalEngine_Facility_ID__c);
      if (vid) map.set(vid, f);
    }
    return map;
  }, [facilities]);

  const csRepOptions = useMemo(() => {
    const names = new Set();
    for (const f of facilities) {
      const n = (f.customerSuccessName || '').trim();
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [facilities]);

  const toggleRowExpansion = (facilityId) => {
    const next = new Set(expandedRows);
    if (next.has(facilityId)) next.delete(facilityId);
    else next.add(facilityId);
    setExpandedRows(next);
  };

  const getAllDescendantIds = (node) => {
    const ids = [];
    if (node.children?.length > 0) {
      node.children.forEach((child) => {
        ids.push(child.id);
        ids.push(...getAllDescendantIds(child));
      });
    }
    return ids;
  };

  const toggleHierarchyExpansion = (nodeId, node) => {
    const next = new Set(expandedHierarchy);
    if (next.has(nodeId)) {
      next.delete(nodeId);
      getAllDescendantIds(node).forEach((id) => next.delete(id));
    } else {
      next.add(nodeId);
      getAllDescendantIds(node).forEach((id) => next.add(id));
    }
    setExpandedHierarchy(next);
  };

  const hasAddress = (f) => Boolean(f && (f.billingStreet || f.billingCity));

  const addressLines = (f) => {
    if (!f) return [];
    const line1 = f.billingStreet || null;
    const cityState = [f.billingCity, f.billingState].filter(Boolean).join(', ');
    const line2 = [cityState, f.billingPostalCode].filter(Boolean).join(' ');
    if (line1) return line2 ? [line1, line2] : [line1];
    return line2 ? [line2] : [];
  };

  const getLoginCount = useCallback((veId) => {
    if (!veId) return 0;
    return loginCounts.get(veId) || 0;
  }, [loginCounts]);

  const facilityPassesFilters = useCallback((f, { ignoreSearch = false } = {}) => {
    if (!f) return false;

    if (showMissingAddressOnly && hasAddress(f)) return false;

    if (payingOnly) {
      const vid = formatFacilityId(f.vitalEngine_Facility_ID__c);
      if (!vid || !payingFacilityIds.has(vid)) return false;
    }

    if (csRepFilter === '__unassigned__') {
      if ((f.customerSuccessName || '').trim()) return false;
    } else if (csRepFilter) {
      if ((f.customerSuccessName || '').trim() !== csRepFilter) return false;
    }

    if (activeFilterEnabled) {
      const vid = formatFacilityId(f.vitalEngine_Facility_ID__c);
      const count = getLoginCount(vid);
      const min = Math.max(0, Number(minLogins) || 0);
      if (count < min) return false;
    }

    if (!ignoreSearch) {
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        const haystacks = [
          f.name,
          f.billingStreet,
          f.billingCity,
          f.billingState,
          f.billingPostalCode,
          f.billingCountry,
          f.customerSuccessName,
          f.salesRepName,
          f.customer_Segment__c,
          f.specialty__c,
          f.idN__c,
          f.type,
          f.vitalEngine_Facility_ID__c,
          formatFacilityId(f.vitalEngine_Facility_ID__c),
        ];
        if (!haystacks.some((v) => v && String(v).toLowerCase().includes(q))) return false;
      }
    }

    return true;
  }, [
    showMissingAddressOnly,
    payingOnly,
    payingFacilityIds,
    csRepFilter,
    activeFilterEnabled,
    getLoginCount,
    minLogins,
    searchQuery,
  ]);

  const filtersActive = activeFilterEnabled || payingOnly || Boolean(csRepFilter)
    || showMissingAddressOnly || searchQuery.trim().length > 0;

  const missingAddressCount = useMemo(
    () => facilities.reduce((n, f) => (hasAddress(f) ? n : n + 1), 0),
    [facilities],
  );

  const filteredFacilities = useMemo(() => {
    const out = facilities.filter((f) => facilityPassesFilters(f));
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmpStr = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' }) * dir;
    const cmpNum = (a, b) => {
      const na = a == null || a === '' || Number.isNaN(Number(a)) ? -Infinity : Number(a);
      const nb = b == null || b === '' || Number.isNaN(Number(b)) ? -Infinity : Number(b);
      if (na === nb) return 0;
      return na < nb ? -1 * dir : 1 * dir;
    };

    out.sort((a, b) => {
      switch (sortKey) {
        case 'veId':
          return cmpNum(
            formatFacilityId(a.vitalEngine_Facility_ID__c),
            formatFacilityId(b.vitalEngine_Facility_ID__c),
          );
        case 'segment':
          return cmpStr(a.customer_Segment__c, b.customer_Segment__c);
        case 'beds':
          return cmpNum(a.number_of_Beds__c, b.number_of_Beds__c);
        case 'cs':
          return cmpStr(a.customerSuccessName, b.customerSuccessName);
        case 'logins':
          return cmpNum(
            getLoginCount(formatFacilityId(a.vitalEngine_Facility_ID__c)),
            getLoginCount(formatFacilityId(b.vitalEngine_Facility_ID__c)),
          );
        case 'name':
        default:
          return cmpStr(a.name, b.name);
      }
    });
    return out;
  }, [facilities, facilityPassesFilters, sortKey, sortDir, getLoginCount]);

  const facilityFromHierarchyNode = useCallback((node) => {
    const vid = formatFacilityId(node.vitalEngine_Facility_ID__c);
    if (vid && facilityByVeId.has(vid)) return facilityByVeId.get(vid);
    // Fall back to node fields so search still works on hierarchy-only props
    return {
      name: node.name,
      billingCity: node.billingCity,
      billingState: node.billingState,
      vitalEngine_Facility_ID__c: node.vitalEngine_Facility_ID__c,
      customerSuccessName: undefined,
      billingStreet: undefined,
    };
  }, [facilityByVeId]);

  const hierarchyNodeSelfMatches = useCallback((node) => {
    return facilityPassesFilters(facilityFromHierarchyNode(node));
  }, [facilityPassesFilters, facilityFromHierarchyNode]);

  const hierarchyMatchesFilter = useCallback((node) => {
    if (hierarchyNodeSelfMatches(node)) return true;
    return node.children?.some(hierarchyMatchesFilter) || false;
  }, [hierarchyNodeSelfMatches]);

  const autoExpandForFilter = useMemo(() => {
    if (!filtersActive) return new Set();
    const set = new Set();
    const walk = (node, ancestors) => {
      if (hierarchyNodeSelfMatches(node)) {
        ancestors.forEach((a) => set.add(a));
        set.add(node.id);
      }
      if (node.children) {
        node.children.forEach((c) => walk(c, [...ancestors, node.id]));
      }
    };
    hierarchy.forEach((n) => walk(n, []));
    return set;
  }, [hierarchy, filtersActive, hierarchyNodeSelfMatches]);

  const isHierarchyExpanded = (id) =>
    expandedHierarchy.has(id) || autoExpandForFilter.has(id);

  const hierarchyMatchCount = useMemo(() => {
    if (!filtersActive) return 0;
    let count = 0;
    const walk = (node) => {
      if (hierarchyNodeSelfMatches(node)) count += 1;
      node.children?.forEach(walk);
    };
    hierarchy.forEach(walk);
    return count;
  }, [hierarchy, filtersActive, hierarchyNodeSelfMatches]);

  const totalHierarchyNodes = useMemo(() => {
    let count = 0;
    const walk = (node) => {
      count += 1;
      node.children?.forEach(walk);
    };
    hierarchy.forEach(walk);
    return count;
  }, [hierarchy]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const exportFilteredFacilities = () => {
    setExporting(true);
    setError('');
    try {
      const rows = filteredFacilities.map((f) => {
        const vid = formatFacilityId(f.vitalEngine_Facility_ID__c);
        const withLogins = { ...f, _loginCount: activeFilterEnabled ? getLoginCount(vid) : undefined };
        const obj = {};
        for (const [header, getter] of EXPORT_COLUMNS) {
          if (header === 'Logins in Range' && !activeFilterEnabled) continue;
          const v = getter(withLogins);
          obj[header] = v == null ? '' : v;
        }
        return obj;
      });
      const headers = Object.keys(rows[0] || Object.fromEntries(
        EXPORT_COLUMNS.filter(([h]) => activeFilterEnabled || h !== 'Logins in Range')
          .map(([h]) => [h, '']),
      ));
      const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{}], { header: headers });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, 'Facilities');
      const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      triggerDownload(blob, `All-Facilities-Filtered-${formatYmd(new Date())}.xlsx`);
    } catch (e) {
      setError(`Export failed: ${e.message || 'unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const renderHierarchyNode = (node, level = 0, ancestorMatched = false) => {
    if (filtersActive && !ancestorMatched && !hierarchyMatchesFilter(node)) return null;

    const hasChildren = node.children?.length > 0;
    const isExpanded = isHierarchyExpanded(node.id);
    const childAncestorMatched = ancestorMatched || hierarchyNodeSelfMatches(node);
    const vid = formatFacilityId(node.vitalEngine_Facility_ID__c);
    const loginN = activeFilterEnabled ? getLoginCount(vid) : null;

    return (
      <div key={node.id} className="hierarchy-node" style={{ marginLeft: `${level * 2}rem` }}>
        <div className="hierarchy-node-content">
          {hasChildren ? (
            <button type="button" className="hierarchy-expand-button" onClick={() => toggleHierarchyExpansion(node.id, node)}>
              {isExpanded ? '▼' : '▶'}
            </button>
          ) : (
            <span className="hierarchy-spacer" />
          )}
          <span className={`hierarchy-node-name ${hasChildren ? 'has-children' : ''}`}>
            {level > 0 && '└─ '}
            {node.name}
          </span>
          {vid && (
            <span className="hierarchy-node-id">ID: {vid}</span>
          )}
          {node.billingCity && node.billingState && (
            <span className="hierarchy-node-location">{node.billingCity}, {node.billingState}</span>
          )}
          {activeFilterEnabled && vid && (
            <span className="hierarchy-node-logins" title={`Logins ${startDate} → ${endDate}`}>
              {loginN} login{loginN === 1 ? '' : 's'}
            </span>
          )}
          {vid && (
            <AnalysisShortcutBar
              context={{
                kind: 'facility',
                id: vid,
                label: node.name,
              }}
              compact
            />
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="hierarchy-children">
            {node.children.map((child) => renderHierarchyNode(child, level + 1, childAncestorMatched))}
          </div>
        )}
      </div>
    );
  };

  const renderDetailRow = (facility) => {
    const fields = [
      { label: 'Salesforce ID', value: facility.id },
      { label: 'VitalEngine Facility ID', value: facility.vitalEngine_Facility_ID__c },
      { label: 'Parent ID', value: facility.parentId },
      { label: 'Type', value: facility.type },
      { label: 'Billing Street', value: facility.billingStreet },
      { label: 'Billing City', value: facility.billingCity },
      { label: 'Billing State', value: facility.billingState },
      { label: 'Billing Postal Code', value: facility.billingPostalCode },
      { label: 'Billing Country', value: facility.billingCountry },
      {
        label: 'Latitude',
        value: facility.billingLatitude != null ? facility.billingLatitude.toFixed(6) : null,
      },
      {
        label: 'Longitude',
        value: facility.billingLongitude != null ? facility.billingLongitude.toFixed(6) : null,
      },
      { label: 'Geocode Source', value: facility.geocodeSource },
      {
        label: 'Geocoded At',
        value: facility.geocodedAt
          ? new Date(facility.geocodedAt).toLocaleString()
          : null,
      },
      { label: 'IDN', value: facility.idN__c },
      { label: 'Installation Date', value: facility.vitalEngine_Installation_Date__c },
      { label: 'Customer Segment', value: facility.customer_Segment__c },
      { label: 'Number of Beds', value: facility.number_of_Beds__c },
      { label: 'EMR Type', value: facility.emR_Type__c },
      { label: 'Specialty', value: facility.specialty__c },
      { label: 'Owner ID', value: facility.ownerId },
      { label: 'Owner Name', value: facility.ownerName },
      { label: 'Owner Email', value: facility.ownerEmail },
      { label: 'Customer Success ID', value: facility.customer_Success__c },
      { label: 'Customer Success Name', value: facility.customerSuccessName },
      { label: 'Customer Success Email', value: facility.customerSuccessEmail },
      { label: 'Sales Rep ID', value: facility.sales_Representative__c },
      { label: 'Sales Rep Name', value: facility.salesRepName },
      { label: 'Sales Rep Email', value: facility.salesRepEmail },
    ];

    return (
      <tr key={`${facility.id}-detail`} className="detail-row">
        <td colSpan={activeFilterEnabled ? 9 : 8}>
          <div className="detail-content">
            <div className="detail-grid">
              {fields.map((field, idx) => (
                <div key={idx} className="detail-field">
                  <span className="detail-label">{field.label}:</span>
                  <span className="detail-value">{field.value || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const hasData = facilities.length > 0 || hierarchy.length > 0;
  const hasSearch = searchQuery.trim().length > 0;

  return (
    <div className="facilities-container">
      <div className="facilities-header">
        <h2>Salesforce Facilities</h2>
        <div className="facilities-tabs">
          <button
            type="button"
            className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
            disabled={!hasData}
          >
            List View
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'hierarchy' ? 'active' : ''}`}
            onClick={() => setActiveTab('hierarchy')}
            disabled={!hasData}
          >
            Hierarchy View
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="facilities-content">
        {loading && initialLoad ? (
          <div className="loading-spinner">
            <div className="spinner" />
            <p>Loading facilities...</p>
          </div>
        ) : !hasData && !loading ? (
          <div className="empty-state">
            <p>No cached facility data. Use the refresh bar at the top of the app to sync from Salesforce.</p>
          </div>
        ) : (
          <>
            <div className="facilities-toolbar">
              <input
                type="text"
                className="facilities-search"
                placeholder={
                  activeTab === 'list'
                    ? 'Search facilities by name, address, ID, CS rep…'
                    : 'Search facilities by name, city, state, or ID…'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search facilities"
              />
              {hasSearch && (
                <button
                  type="button"
                  className="facilities-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
              {activeTab === 'list' && missingAddressCount > 0 && (
                <label className="facilities-filter-toggle" title="Show only facilities with no street/city in Salesforce">
                  <input
                    type="checkbox"
                    checked={showMissingAddressOnly}
                    onChange={(e) => setShowMissingAddressOnly(e.target.checked)}
                  />
                  Missing address only
                  <span className="facilities-filter-count">({missingAddressCount})</span>
                </label>
              )}
              {facilities.length > 0 && (
                <button
                  type="button"
                  className="facilities-export-btn"
                  onClick={exportFilteredFacilities}
                  disabled={exporting || filteredFacilities.length === 0}
                  title="Download currently filtered facilities as Excel"
                >
                  {exporting ? 'Building…' : `Download Excel (${filteredFacilities.length})`}
                </button>
              )}
            </div>

            <div className="facilities-filters">
              <label className="facilities-filter-toggle">
                <input
                  type="checkbox"
                  checked={activeFilterEnabled}
                  onChange={(e) => setActiveFilterEnabled(e.target.checked)}
                />
                Active within date range
              </label>

              {activeFilterEnabled && (
                <>
                  <div className="facilities-filter-field">
                    <label htmlFor="facActivePreset">Range</label>
                    <select
                      id="facActivePreset"
                      className="date-range-dropdown"
                      value={dateRangePreset}
                      onChange={(e) => setDateRangePreset(e.target.value)}
                    >
                      {Object.entries(DATE_PRESET_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  {dateRangePreset === 'custom' && (
                    <DateRangePicker
                      startDate={customStartDate}
                      endDate={customEndDate}
                      onStartDateChange={setCustomStartDate}
                      onEndDateChange={setCustomEndDate}
                    />
                  )}
                  <div className="facilities-filter-field">
                    <label htmlFor="facMinLogins">Min logins</label>
                    <input
                      id="facMinLogins"
                      type="number"
                      min={1}
                      className="facilities-min-logins"
                      value={minLogins}
                      onChange={(e) => setMinLogins(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    />
                  </div>
                  {loginsLoading && <span className="facilities-filter-hint">Loading logins…</span>}
                </>
              )}

              <label className="facilities-filter-toggle" title="Facilities covered by any customer on the Customers page">
                <input
                  type="checkbox"
                  checked={payingOnly}
                  onChange={(e) => setPayingOnly(e.target.checked)}
                  disabled={payingLoading}
                />
                Paying customer
                {payingLoading && <span className="facilities-filter-hint">…</span>}
              </label>

              <div className="facilities-filter-field">
                <label htmlFor="facCsRep">CS Rep assigned</label>
                <select
                  id="facCsRep"
                  className="date-range-dropdown"
                  value={csRepFilter}
                  onChange={(e) => setCsRepFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="__unassigned__">Unassigned</option>
                  {csRepOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            {activeTab === 'list' ? (
              <div className="facilities-list">
                {filteredFacilities.length === 0 ? (
                  <div className="facilities-no-results">
                    {filtersActive
                      ? <>No facilities match the current filters{hasSearch ? <> and search <strong>&ldquo;{searchQuery}&rdquo;</strong></> : null}.</>
                      : <>No facilities to display.</>}
                  </div>
                ) : (
                  <table className="facilities-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }} />
                        <th>
                          <button type="button" className="facilities-sort-btn" onClick={() => toggleSort('name')}>
                            Name{sortIndicator('name')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="facilities-sort-btn" onClick={() => toggleSort('veId')}>
                            VitalEngine ID{sortIndicator('veId')}
                          </button>
                        </th>
                        <th>Address</th>
                        <th>
                          <button type="button" className="facilities-sort-btn" onClick={() => toggleSort('segment')}>
                            Customer Segment{sortIndicator('segment')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="facilities-sort-btn" onClick={() => toggleSort('beds')}>
                            Beds{sortIndicator('beds')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="facilities-sort-btn" onClick={() => toggleSort('cs')}>
                            Customer Success{sortIndicator('cs')}
                          </button>
                        </th>
                        {activeFilterEnabled && (
                          <th>
                            <button type="button" className="facilities-sort-btn" onClick={() => toggleSort('logins')}>
                              Logins{sortIndicator('logins')}
                            </button>
                          </th>
                        )}
                        <th style={{ width: '180px' }}>Analyze</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFacilities.map((facility) => {
                        const veFid = formatFacilityId(facility.vitalEngine_Facility_ID__c);
                        const lines = addressLines(facility);
                        const addressOk = hasAddress(facility);
                        const geocoded =
                          facility.billingLatitude != null &&
                          facility.billingLongitude != null;
                        const loginN = activeFilterEnabled ? getLoginCount(veFid) : null;
                        return (
                          <React.Fragment key={facility.id}>
                            <tr className={expandedRows.has(facility.id) ? 'expanded' : ''}>
                              <td>
                                <button type="button" className="expand-button" onClick={() => toggleRowExpansion(facility.id)}>
                                  {expandedRows.has(facility.id) ? '−' : '+'}
                                </button>
                              </td>
                              <td>{facility.name}</td>
                              <td>{veFid || '-'}</td>
                              <td>
                                {addressOk ? (
                                  <div className="facilities-address-cell">
                                    {lines.map((l, i) => (
                                      <div
                                        key={i}
                                        className={i === 0 ? 'facilities-address-line1' : 'facilities-address-line2'}
                                      >
                                        {l}
                                      </div>
                                    ))}
                                    {geocoded ? (
                                      <span
                                        className={`facilities-geo-chip facilities-geo-${facility.geocodeSource || 'unknown'}`}
                                        title={`Geocoded by ${facility.geocodeSource || 'unknown source'}`}
                                      >
                                        ●
                                      </span>
                                    ) : (
                                      <span
                                        className="facilities-geo-chip facilities-geo-pending"
                                        title="No cached lat/lng — map will geocode on demand"
                                      >
                                        ○
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    className="facilities-noaddress"
                                    title="No street or city in Salesforce — fix in SF and re-refresh, or this facility will never appear on the map."
                                  >
                                    No address
                                  </span>
                                )}
                              </td>
                              <td>{facility.customer_Segment__c || '-'}</td>
                              <td>{facility.number_of_Beds__c || '-'}</td>
                              <td>{facility.customerSuccessName || '-'}</td>
                              {activeFilterEnabled && (
                                <td>{loginN}</td>
                              )}
                              <td>
                                {veFid ? (
                                  <AnalysisShortcutBar
                                    context={{ kind: 'facility', id: veFid, label: facility.name }}
                                    compact
                                  />
                                ) : (
                                  <span className="facilities-noid" title="No VitalEngine facility ID — can't drill into analytics">—</span>
                                )}
                              </td>
                            </tr>
                            {expandedRows.has(facility.id) && renderDetailRow(facility)}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div className="facilities-count">
                  {filtersActive ? (
                    <>
                      Showing <strong>{filteredFacilities.length}</strong> of <strong>{facilities.length}</strong> facilities
                    </>
                  ) : (
                    <>Total: <strong>{facilities.length}</strong> facilities</>
                  )}
                </div>
              </div>
            ) : (
              <div className="hierarchy-view">
                {filtersActive && hierarchyMatchCount === 0 ? (
                  <div className="facilities-no-results">
                    No facilities match the current filters
                    {hasSearch ? <> and search <strong>&ldquo;{searchQuery}&rdquo;</strong></> : null}.
                  </div>
                ) : (
                  hierarchy.map((node) => renderHierarchyNode(node))
                )}
                <div className="facilities-count">
                  {filtersActive ? (
                    <>Matching <strong>{hierarchyMatchCount}</strong> of <strong>{totalHierarchyNodes}</strong> facilities</>
                  ) : (
                    <>Total: <strong>{hierarchy.length}</strong> root facilities ({totalHierarchyNodes} total nodes)</>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Facilities;
